import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatearFecha, fechaParaInput } from '../utils/fecha';
import { estadoDelPrestamo, repartirAbono, repartirEntrePrestamos, conceptoDelAbono } from '../utils/prestamo';
import { imprimirRecibo } from '../utils/recibo';
import { guardarInstantanea, leerInstantanea, encolarPago, leerCola, EVENTO_COLA } from '../utils/offline';
import { buscarClientes } from '../utils/buscar';

// Cuántas veces se cobra el interés entre las dos fechas del préstamo.
// Misma regla que la vista previa de Nuevo Préstamo.
const moneda = (n) => '$' + Math.round(n).toLocaleString('es-CO');

function Payments({ clients, onSuccess }) {
  const [busqueda, setBusqueda] = useState('');
  const [clienteElegido, setClienteElegido] = useState(null);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState('');
  const [amount, setAmount] = useState('');
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().split('T')[0]);
  // Un abono es de interés o de capital, y el programa necesita saber cuál:
  // el interés se calcula sobre el capital que queda.
  //
  // Por defecto se reparte solo: se anota lo que el cliente entregó y el
  // programa cubre primero el interés y baja el capital con el resto. Las otras
  // dos opciones siguen ahí para forzar el destino del abono.
  const [tipoPago, setTipoPago] = useState('auto');
  // Cuánto de lo entregado se cobra de interés al repartir entre varios
  // préstamos. Vacío = el que calculó el programa.
  const [interesCobrado, setInteresCobrado] = useState('');
  const [loans, setLoans] = useState([]);
  // Pagos de cada préstamo del cliente, indexados por id de préstamo.
  const [pagosPorPrestamo, setPagosPorPrestamo] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  // Pago que se está corrigiendo, con los valores del formulario.
  const [pagoEditando, setPagoEditando] = useState(null);
  const [montoEditado, setMontoEditado] = useState('');
  const [fechaEditada, setFechaEditada] = useState('');
  const [tipoEditado, setTipoEditado] = useState('interes');
  const [guardandoPago, setGuardandoPago] = useState(false);
  const [sinSenal, setSinSenal] = useState(!navigator.onLine);
  const [pendientes, setPendientes] = useState(leerCola().length);
  // De cuándo son los datos cuando se trabaja desde la copia local.
  const [fechaInstantanea, setFechaInstantanea] = useState(null);

  useEffect(() => {
    const conectado = () => setSinSenal(false);
    const desconectado = () => setSinSenal(true);
    // La cola la vacía la aplicación entera, no esta pantalla, así que el
    // contador se entera por el aviso y no por su propia cuenta.
    const cambioLaCola = () => {
      setPendientes(leerCola().length);
      cargarHistorial();
    };

    window.addEventListener('online', conectado);
    window.addEventListener('offline', desconectado);
    window.addEventListener(EVENTO_COLA, cambioLaCola);
    return () => {
      window.removeEventListener('online', conectado);
      window.removeEventListener('offline', desconectado);
      window.removeEventListener(EVENTO_COLA, cambioLaCola);
    };
  }, []);

  // Sin señal la lista de clientes viene de la copia guardada; con señal,
  // siempre de la que trae la aplicación.
  const instantanea = sinSenal ? leerInstantanea() : null;
  const listaClientes = instantanea && clients.length === 0 ? instantanea.clientes : clients;

  const coincidencias = buscarClientes(listaClientes, busqueda);

  const cargarHistorial = () => {
    axios.get('/api/payments-history')
      .then(res => setPaymentHistory(res.data || []))
      .catch(err => console.error('Error:', err));
  };

  useEffect(cargarHistorial, []);

  // Al elegir cliente, traer sus préstamos. Si solo tiene uno, se selecciona
  // solo para ahorrar un clic.
  useEffect(() => {
    if (!clienteElegido) {
      setLoans([]);
      return;
    }
    axios.get(`/api/loans/${clienteElegido.id}`)
      .then(res => {
        const lista = res.data || [];
        setLoans(lista);
        setFechaInstantanea(null);
        if (lista.length === 1) setSelectedLoan(String(lista[0].id));
      })
      .catch(() => {
        // Sin conexión se tira de la copia local, diciendo de cuándo es.
        const copia = leerInstantanea();
        const lista = (copia && copia.prestamosPorCliente[clienteElegido.id]) || [];
        setLoans(lista);
        setFechaInstantanea(copia ? copia.fecha : null);
        if (lista.length === 1) setSelectedLoan(String(lista[0].id));
      });
  }, [clienteElegido]);

  // Traer los pagos de todos los préstamos del cliente, para poder mostrar su
  // historial completo y no solo el del préstamo seleccionado.
  const cargarPagosDelCliente = useCallback(() => {
    if (loans.length === 0) {
      setPagosPorPrestamo({});
      return;
    }
    Promise.all(
      loans.map(l =>
        axios.get(`/api/payments/${l.id}`)
          .then(res => [l.id, Array.isArray(res.data) ? res.data : []])
          .catch(() => [l.id, []])
      )
    ).then(pares => setPagosPorPrestamo(Object.fromEntries(pares)));
  }, [loans]);

  // Copia local para poder cobrar sin señal. Se guarda con conexión, que es
  // cuando los datos son de fiar, e incluye los préstamos de cada cliente
  // porque sin ellos no se puede ni elegir a qué préstamo va el abono.
  //
  // En una sola petición: antes pedía los préstamos cliente por cliente, y con
  // 114 clientes eran 114 peticiones cada vez que se abría esta pantalla.
  useEffect(() => {
    if (sinSenal || clients.length === 0) return;

    let cancelado = false;
    axios.get('/api/clients/resumen')
      .then(res => {
        if (cancelado) return;
        const porCliente = {};
        (res.data.clientes || []).forEach(c => { porCliente[c.id] = c.prestamos || []; });
        guardarInstantanea(clients, porCliente);
      })
      .catch(() => { /* sin copia es peor, pero no rompe la pantalla */ });

    return () => { cancelado = true; };
  }, [clients, sinSenal]);

  useEffect(cargarPagosDelCliente, [cargarPagosDelCliente]);

  const prestamo = loans.find(l => String(l.id) === String(selectedLoan));
  const pagosDelPrestamo = pagosPorPrestamo[selectedLoan] || [];

  const cuentas = prestamo ? estadoDelPrestamo(prestamo, pagosDelPrestamo) : null;

  // Cuando el cliente tiene varios préstamos puede entregar un solo monto para
  // todos. Se elige en el mismo desplegable, con este valor en vez de un id.
  const TODOS = 'todos';
  const varios = loans.length > 1;
  const enTodos = selectedLoan === TODOS;

  const estadosDeTodos = loans.map(l => ({
    prestamo: l,
    estado: estadoDelPrestamo(l, pagosPorPrestamo[l.id] || [])
  }));

  const debeEnTotal = estadosDeTodos.reduce((s, e) => s + e.estado.deudaTotal, 0);
  const interesDeTodos = estadosDeTodos.reduce(
    (s, e) => s + Math.max(0, e.estado.interesPendiente), 0
  );

  // EL MES COMPLETO de todos: capital por tasa, préstamo por préstamo.
  //
  // No es lo mismo que `interesDeTodos`. Ése es el que el motor ya generó, y es
  // CERO mientras ningún préstamo haya cortado. A Rubén Sola le pasó: sus tres
  // créditos cortaban más adelante, el programa calculó $0 de interés y los
  // $850.000 se iban enteros al capital. La cuenta no estaba mal —es que no se
  // cobró el mes— pero nadie lo veía.
  //
  // Ésta es la cifra que el prestamista tiene en la cabeza cuando dice «cóbrese
  // el interés»: para Rubén, $130.000 + $10.000 + $10.000 = $150.000.
  const mesDeTodos = estadosDeTodos.reduce(
    (s, e) => s + Math.round(Math.max(0, e.estado.capitalPendiente) *
                             ((Number(e.prestamo.interest_rate) || 0) / 100)), 0
  );

  // Historial del cliente: todos sus pagos, del más reciente al más antiguo,
  // indicando a qué préstamo pertenece cada uno.
  const historialCliente = loans
    .flatMap(l => (pagosPorPrestamo[l.id] || []).map(p => ({ ...p, prestamo: l })))
    .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));

  // Cuánto lleva sin pagar y si eso supera el periodo pactado.
  let atraso = null;
  if (prestamo) {
    const diasPorPeriodo = { daily: 1, weekly: 7, biweekly: 15, monthly: 30 };
    const periodo = diasPorPeriodo[prestamo.frequency] || 30;
    const ultimo = (pagosPorPrestamo[prestamo.id] || [])
      .map(p => new Date(p.payment_date))
      .sort((a, b) => b - a)[0];
    const referencia = ultimo || new Date(prestamo.start_date);
    const dias = Math.floor((new Date() - referencia) / 86400000);
    atraso = {
      dias,
      desdeUltimoPago: Boolean(ultimo),
      periodo,
      retrasado: dias > periodo
    };
  }

  // Al seleccionar un préstamo se propone la cuota, que es lo que se cobra
  // habitualmente. El campo queda editable y el saldo total está a un clic.
  // Al elegir préstamo se propone el interés que debe, que es el cobro
  // habitual.
  //
  // El efecto NO depende de `tipoPago`. Cuando dependía, los atajos del
  // recuadro quedaban inservibles: «Saldar capital» ponía el monto y el efecto
  // lo borraba un instante después, al ver que el tipo había cambiado. Ahora
  // cada botón decide qué monto propone, y el efecto solo actúa al cambiar de
  // préstamo o al llegar un abono nuevo.
  useEffect(() => {
    if (enTodos) {
      // Repartiendo entre todos se propone el interés de todos: es lo que hay
      // que cubrir para que ninguno siga acumulando atraso.
      setTipoPago('auto');
      setInteresCobrado('');
      setAmount(interesDeTodos > 0 ? String(Math.round(interesDeTodos)) : '');
      return;
    }
    if (!cuentas) return;
    setTipoPago('auto');
    setAmount(String(Math.round(Math.max(cuentas.interesPendiente, cuentas.interesPorCobro))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLoan, pagosDelPrestamo.length]);

  // Cambiar de tipo desde el selector propone el monto que corresponde: el
  // interés que debe, o nada para un abono a capital, donde la cifra la pone
  // quien cobra.
  const elegirTipo = (tipo) => {
    setTipoPago(tipo);
    if (!cuentas) return;
    setAmount(tipo === 'capital'
      ? ''
      : String(Math.round(Math.max(cuentas.interesPendiente, cuentas.interesPorCobro))));
  };

  // Cómo quedaría repartido lo que se ha escrito en el monto. Se recalcula
  // mientras se teclea para que quien cobra vea el reparto antes de guardar,
  // no después.
  const reparto = cuentas && tipoPago === 'auto' && parseFloat(amount) > 0
    ? repartirAbono(parseFloat(amount), cuentas)
    : null;

  // El mismo cálculo, pero cuando lo entregado se reparte entre varios
  // préstamos. Aquí no hay tipo que elegir: repartir a mano entre créditos es
  // justo lo que se quiere evitar.
  // El interés pactado a mano, si se escribió. Se calcula UNA vez y se usa en
  // la vista previa y al guardar: dos lecturas del campo es como la pantalla
  // termina enseñando un reparto y registrando otro.
  const opcionesDelReparto = interesCobrado !== '' && parseFloat(interesCobrado) >= 0
    ? { interes: parseFloat(interesCobrado) }
    : {};

  const repartoTodos = enTodos && parseFloat(amount) > 0
    ? repartirEntrePrestamos(parseFloat(amount), estadosDeTodos, opcionesDelReparto)
    : null;

  const elegirCliente = (c) => {
    setClienteElegido(c);
    setBusqueda(c.name);
    setMostrarSugerencias(false);
    setSelectedLoan('');
    setAmount('');
  };

  const abrirCorreccion = (pago) => {
    setPagoEditando(pago);
    setMontoEditado(String(pago.amount ?? ''));
    setFechaEditada(fechaParaInput(pago.payment_date));
    setTipoEditado(pago.tipo === 'capital' ? 'capital' : 'interes');
  };

  // Tras corregir o borrar hay que refrescar tres cosas: los pagos del cliente
  // (de donde sale el atraso), el historial general y el panel, porque el score
  // se recalcula en el servidor.
  const refrescarTodo = () => {
    cargarPagosDelCliente();
    cargarHistorial();
    if (onSuccess) onSuccess();
  };

  const guardarCorreccion = async (e) => {
    e.preventDefault();
    const monto = parseFloat(montoEditado);
    if (!Number.isFinite(monto) || monto <= 0) {
      alert('El monto debe ser un número mayor que cero');
      return;
    }

    setGuardandoPago(true);
    try {
      await axios.put(`/api/payments/${pagoEditando.id}`, {
        amount: monto,
        tipo: tipoEditado,
        payment_date: fechaEditada
          ? new Date(`${fechaEditada}T00:00:00`).toISOString()
          : null
      });
      setPagoEditando(null);
      refrescarTodo();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
    setGuardandoPago(false);
  };

  const borrarPago = async (pago) => {
    const ok = window.confirm(
      `¿Borrar el pago de ${moneda(pago.amount)} del ${formatearFecha(pago.payment_date)}?\n\n` +
      'Se recalculará el score del cliente y su estado de mora.'
    );
    if (!ok) return;

    try {
      await axios.delete(`/api/payments/${pago.id}`);
      refrescarTodo();
    } catch (err) {
      alert('❌ ' + (err.response?.data?.error || err.message));
    }
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    // EL FRENO AL DOBLE CLIC.
    //
    // A un cliente le quedaron CINCO abonos de $2.000.000 el mismo día sobre un
    // préstamo de $2.000.000. Nadie cobró cinco veces: el botón no se bloqueaba
    // ni cambiaba de texto, la conexión iba lenta, y quien cobraba volvió a
    // tocar. Sin nada en pantalla que diga «voy», tocar otra vez es lo sensato.
    //
    // El mismo estado ya frenaba el cuadro de corregir un pago. Al botón
    // principal —el que se usa todos los días— se le había olvidado.
    if (guardandoPago) return;
    if (!selectedLoan || !amount) {
      alert('Selecciona el préstamo e indica el monto');
      return;
    }
    setGuardandoPago(true);
    try {

    const fecha = new Date(`${fechaPago}T00:00:00`).toISOString();
    const anotarEn = (idPrestamo, monto, tipo) => ({
      loan_id: parseInt(idPrestamo),
      amount: monto,
      tipo,
      payment_date: fecha
    });
    const anotar = (monto, tipo) => anotarEn(selectedLoan, monto, tipo);

    // Con reparto automático lo entregado puede partirse en dos anotaciones:
    // una de interés y otra de capital. Se guardan por separado porque son
    // cosas distintas —una baja la deuda y la otra no— y porque el historial
    // tiene que poder explicar después en qué se fue el dinero.
    // Los préstamos que el reparto deja en cero NO se cierran con un abono.
    //
    // Se probó contra el servidor: un abono normal en un préstamo que todavía
    // no corta se queda esperando al corte, el capital sigue marcando lo mismo
    // y el préstamo sigue abierto. La pantalla prometía «queda saldado» y no
    // pasaba. Van por el camino del cierre, que es el que sí los cierra y el
    // único donde se puede cobrar el mes completo de un préstamo de tres días.
    let cierres = [];
    let pagos;
    if (enTodos) {
      const r = repartirEntrePrestamos(parseFloat(amount), estadosDeTodos, opcionesDelReparto);
      if (r.sobrante > 0) {
        const seguir = window.confirm(
          `${moneda(parseFloat(amount))} es más de lo que ${clienteElegido.name} debe ` +
          `por sus ${loans.length} préstamos (${moneda(debeEnTotal)}).\n\n` +
          `Sobran ${moneda(r.sobrante)}.\n\n` +
          '¿Registrarlo igual? Si es un pago para quedar a paz y salvo, mejor ' +
          'usa Deuda cliente, que deja constancia del acuerdo.'
        );
        if (!seguir) return;
      }

      // Cerrar un préstamo NO SE PUEDE DESHACER, así que se pregunta nombrando
      // uno por uno cuáles se van a cerrar. Un cierre que ocurre de sorpresa
      // dentro de un cobro corriente es de lo peor que puede hacer un programa
      // de plata.
      cierres = r.partes.filter(p => p.cierra);
      if (cierres.length > 0) {
        const lista = cierres.map(p =>
          `   · ${moneda(p.prestamo.amount)} del ${formatearFecha(p.prestamo.start_date)}` +
          ` — recibe ${moneda(p.aInteres + p.aCapital)}` +
          (p.aInteres > 0 ? ` (${moneda(p.aInteres)} de interés)` : '')
        ).join('\n');
        const seguir = window.confirm(
          `Con este pago ${cierres.length === 1 ? 'queda saldado 1 préstamo' :
            `quedan saldados ${cierres.length} préstamos`} de ${clienteElegido.name}:\n\n` +
          `${lista}\n\n` +
          `${cierres.length === 1 ? 'Se cerrará' : 'Se cerrarán'} y ${cierres.length === 1 ?
            'dejará' : 'dejarán'} de generar intereses. Esto no se puede deshacer.\n\n` +
          '¿Seguimos?'
        );
        if (!seguir) return;
      }

      pagos = r.partes.filter(p => !p.cierra).flatMap(p => [
        p.aInteres > 0 ? anotarEn(p.prestamo.id, p.aInteres, 'interes') : null,
        p.aCapital > 0 ? anotarEn(p.prestamo.id, p.aCapital, 'capital') : null
      ].filter(Boolean));
    } else if (tipoPago === 'auto') {
      const r = repartirAbono(parseFloat(amount), cuentas);
      if (r.sobrante > 0) {
        const seguir = window.confirm(
          `${moneda(parseFloat(amount))} es más de lo que ${clienteElegido.name} debe ` +
          `(${moneda(cuentas.deudaTotal)}).\n\n` +
          `Sobran ${moneda(r.sobrante)}.\n\n` +
          '¿Registrarlo igual? Si es un pago para quedar a paz y salvo, mejor ' +
          'usa Deuda cliente, que deja constancia del acuerdo.'
        );
        if (!seguir) return;
      }
      pagos = [
        r.aInteres > 0 ? anotar(r.aInteres, 'interes') : null,
        r.aCapital > 0 ? anotar(r.aCapital, 'capital') : null
      ].filter(Boolean);
    } else {
      pagos = [anotar(parseFloat(amount), tipoPago)];
    }

    if (pagos.length === 0) {
      alert('El monto debe ser mayor que cero');
      return;
    }

    const limpiarFormulario = () => {
      setAmount('');
      setInteresCobrado('');
      setTipoPago('auto');
      setSelectedLoan('');
      setClienteElegido(null);
      setBusqueda('');
      setFechaPago(new Date().toISOString().split('T')[0]);
    };

    const guardarParaDespues = () => {
      let enCola = 0;
      for (const p of pagos) enCola = encolarPago(p);
      if (enCola === 0) {
        alert('❌ No se pudo guardar el cobro: no queda espacio en el teléfono.\n\n' +
              'Conéctate a internet y vuelve a intentarlo.');
        return;
      }
      setPendientes(enCola);
      limpiarFormulario();
      alert(`📴 Sin señal: el cobro quedó anotado en el teléfono.\n\n` +
            `Se subirá solo cuando vuelva la conexión. Pendientes: ${enCola}.`);
    };

    // Sin señal no se cierra nada. Un cierre no se puede deshacer y la cola de
    // pendientes solo sabe guardar abonos: se subiría el abono y el préstamo se
    // quedaría abierto, que es justo la mentira que se vino a quitar.
    if (!navigator.onLine) {
      if (cierres.length > 0) {
        alert('📴 Sin señal no se pueden cerrar préstamos.\n\n' +
              'Este pago deja ' + (cierres.length === 1 ? 'uno saldado' :
                cierres.length + ' saldados') + ', y cerrarlos no se puede deshacer.\n\n' +
              'Espere a tener conexión, o registre los abonos préstamo por préstamo.');
        return;
      }
      return guardarParaDespues();
    }

    try {
      for (const p of pagos) await axios.post('/api/payments', p);

      // Los que quedan en cero se cierran de verdad, por el camino del cierre.
      // Con un abono normal el préstamo seguiría abierto —y si su mes todavía
      // no ha cortado, ni siquiera le bajaría el capital— y la pantalla habría
      // prometido un «paz y salvo» que no ocurre.
      for (const c of cierres) {
        await axios.post(`/api/loans/${c.prestamo.id}/cerrar`, {
          recibido: c.aInteres + c.aCapital,
          interes: c.aInteres,
          capital: c.aCapital,
          nota: `Cierre dentro de un pago repartido entre ${loans.length} préstamos`,
        });
      }

      if (enTodos) {
        const r = repartirEntrePrestamos(parseFloat(amount), estadosDeTodos, opcionesDelReparto);
        alert(
          `✅ ${moneda(parseFloat(amount))} repartidos entre ${r.partes.length} ` +
          `${r.partes.length === 1 ? 'préstamo' : 'préstamos'}:\n\n` +
          r.partes.map(p => {
            const trozos = [];
            if (p.aInteres > 0) trozos.push(`interés ${moneda(p.aInteres)}`);
            if (p.aCapital > 0) trozos.push(`capital ${moneda(p.aCapital)}`);
            return `   ${moneda(p.prestamo.amount)} del ${formatearFecha(p.prestamo.start_date)}: ` +
                   trozos.join(' · ') + (p.cierra ? '  ✅ CERRADO' : '');
          }).join('\n') +
          (r.saldados > 0
            ? `\n\n${r.saldados === 1 ? 'Un préstamo quedó cerrado' : `${r.saldados} préstamos quedaron cerrados`} ` +
              'y ya no genera' + (r.saldados === 1 ? '' : 'n') + ' intereses.'
            : '')
        );
        limpiarFormulario();
        cargarHistorial();
        if (onSuccess) onSuccess();
        return;
      }

      // Si ese abono termina de saldar la deuda hay que decirlo aquí mismo.
      // El aviso de «saldado» vivía en la tarjeta de estado, pero al registrar
      // el pago el formulario se limpia y la tarjeta desaparece: nadie lo veía
      // nunca, y el préstamo se quedaba abierto para siempre ensuciando las
      // listas.
      const despues = estadoDelPrestamo(prestamo, [...pagosDelPrestamo, ...pagos]);

      if (despues.capitalPendiente === 0 && despues.interesPendiente <= 0) {
        const cerrar = window.confirm(
          `✅ ${clienteElegido.name} terminó de pagar este préstamo.\n\n` +
          `Capital: ${moneda(prestamo.amount)} · pagado completo\n\n` +
          `¿Cerrarlo ahora? Dejará de generar intereses y saldrá de Morosos y ` +
          `de Vencimientos.`
        );
        if (cerrar) {
          try {
            await axios.post(`/api/loans/${prestamo.id}/cerrar`, {
              recibido: 0, interes: 0, capital: 0, condonado: 0
            });
          } catch (errCierre) {
            alert('El pago quedó registrado, pero no se pudo cerrar el préstamo: ' +
                  (errCierre.response?.data?.error || errCierre.message) +
                  '\n\nPuedes cerrarlo desde la pantalla de Deuda.');
          }
        }
      } else if (pagos.length > 1) {
        // Se dice cómo se repartió: quien cobra acaba de escribir un solo
        // número y en el historial van a aparecer dos renglones.
        alert(
          `✅ ${moneda(parseFloat(amount))} registrados y repartidos:\n\n` +
          pagos.map(p => `   ${p.tipo === 'capital' ? 'Capital' : 'Interés'}: ${moneda(p.amount)}`).join('\n') +
          `\n\nQueda debiendo ${moneda(despues.deudaTotal)}.`
        );
      } else {
        alert('✅ Pago registrado correctamente');
      }

      limpiarFormulario();
      cargarHistorial();
      if (onSuccess) onSuccess();
    } catch (err) {
      // Sin respuesta del servidor es un problema de red, no un rechazo: el
      // cobro se guarda en vez de perderse. `navigator.onLine` no basta —el
      // teléfono puede tener wifi sin salida a internet.
      if (!err.response) return guardarParaDespues();
      alert('❌ Error: ' + (err.response?.data?.error || err.message));
    }
    } finally {
      // Se suelta pase lo que pase: si un error dejara el botón bloqueado,
      // habría que recargar la página para poder cobrar.
      setGuardandoPago(false);
    }
  };

  return (
    <div className="payments-page">
      <h2>💳 Registrar Pagos</h2>

      {/* Sin señal se puede seguir cobrando, pero hay que decirlo: la peor
          versión de esto es que alguien crea que subió un cobro y no. */}
      {sinSenal && (
        <div style={{
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px',
          padding: '14px 16px', marginBottom: '16px', color: '#92400e', fontSize: '14px'
        }}>
          <strong>📴 Sin conexión.</strong> Puedes seguir anotando cobros: se
          guardan en el teléfono y se suben solos cuando vuelva la señal.
          {fechaInstantanea && (
            <div style={{ marginTop: '6px', fontSize: '13px' }}>
              Los saldos que ves son del <strong>{formatearFecha(fechaInstantanea)}</strong>,
              la última vez que hubo internet. Pueden haber cambiado.
            </div>
          )}
        </div>
      )}

      {pendientes > 0 && (
        <div style={{
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px',
          padding: '14px 16px', marginBottom: '16px', color: '#1e40af', fontSize: '14px'
        }}>
          <strong>
            {pendientes === 1
              ? '1 cobro esperando a subirse'
              : `${pendientes} cobros esperando a subirse`}
          </strong>
          {sinSenal
            ? '. Se enviarán en cuanto vuelva la conexión.'
            : '. Enviándolos...'}
        </div>
      )}

      <div className="form-card">
        <h3>Nuevo Pago</h3>
        <form onSubmit={handleRegisterPayment}>

          <div className="form-group">
            <label>🔍 Buscar Cliente (Nombre o Cédula) *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Ej: Juan López o 1234567890"
                value={busqueda}
                onChange={(e) => {
                  setBusqueda(e.target.value);
                  setMostrarSugerencias(true);
                  setClienteElegido(null);
                }}
                onFocus={() => busqueda && setMostrarSugerencias(true)}
              />

              {mostrarSugerencias && busqueda && coincidencias.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  marginTop: '5px',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  zIndex: 10,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}>
                  {coincidencias.map(c => (
                    <div
                      key={c.id}
                      onClick={() => elegirCliente(c)}
                      style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                    >
                      <strong>{c.name}</strong>
                      {c.cedula && <span style={{ color: '#6b7280', marginLeft: '10px' }}>({c.cedula})</span>}
                      {c.phone && <div style={{ fontSize: '12px', color: '#6b7280' }}>{c.phone}</div>}
                    </div>
                  ))}
                </div>
              )}

              {mostrarSugerencias && busqueda && coincidencias.length === 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px',
                  marginTop: '5px', padding: '10px', color: '#991b1b', fontSize: '14px', zIndex: 10
                }}>
                  ❌ No se encontraron clientes
                </div>
              )}
            </div>
            {clienteElegido && (
              <small style={{ color: '#10b981' }}>
                ✅ Cliente: {clienteElegido.name} {clienteElegido.cedula && `(${clienteElegido.cedula})`}
              </small>
            )}
          </div>

          {clienteElegido && loans.length === 0 && (
            <div style={{
              background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px',
              padding: '12px', marginBottom: '15px', color: '#92400e', fontSize: '14px'
            }}>
              ⚠️ Este cliente no tiene préstamos activos
            </div>
          )}

          {loans.length > 0 && (
            <div className="form-group">
              <label>Préstamo *</label>
              <select
                value={selectedLoan}
                onChange={(e) => setSelectedLoan(e.target.value)}
                required
              >
                <option value="">-- Selecciona préstamo --</option>
                {/* Con varios préstamos, lo normal es que el cliente entregue
                    un solo monto para todo. Va primero porque es el caso
                    habitual, no la excepción. */}
                {varios && (
                  <option value={TODOS}>
                    🔀 Repartir entre sus {loans.length} préstamos (debe {moneda(debeEnTotal)})
                  </option>
                )}
                {loans.map(l => (
                  <option key={l.id} value={l.id}>
                    {moneda(l.amount)} - {l.interest_rate}% ({formatearFecha(l.start_date)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Repartiendo entre todos, lo que hay que ver es qué debe cada
              préstamo, EN EL ORDEN EN QUE SE VA A BARRER: del más chico al más
              grande. Si la tabla y el reparto salieran en orden distinto, nadie
              podría seguir por qué se cerró uno y otro no. */}
          {enTodos && (
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '15px', marginBottom: '15px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#1f2937' }}>
                💵 {clienteElegido?.name} debe {moneda(debeEnTotal)} en {loans.length} préstamos
              </div>
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: '#6b7280', textAlign: 'left' }}>
                    <th style={{ padding: '4px 0', fontWeight: 'normal' }}>Préstamo</th>
                    <th style={{ padding: '4px 0', fontWeight: 'normal', textAlign: 'right' }}>Interés</th>
                    <th style={{ padding: '4px 0', fontWeight: 'normal', textAlign: 'right' }}>Capital</th>
                  </tr>
                </thead>
                <tbody>
                  {[...estadosDeTodos]
                    .sort((a, b) =>
                      (Math.round(a.estado.capitalPendiente) - Math.round(b.estado.capitalPendiente))
                      || (new Date(a.prestamo.start_date) - new Date(b.prestamo.start_date))
                      || ((Number(a.prestamo.id) || 0) - (Number(b.prestamo.id) || 0)))
                    .map(({ prestamo: p, estado }) => (
                      <tr key={p.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '6px 0' }}>
                          {moneda(p.amount)} al {p.interest_rate}%
                          <span style={{ color: '#9ca3af' }}> · {formatearFecha(p.start_date)}</span>
                        </td>
                        <td style={{
                          padding: '6px 0', textAlign: 'right',
                          color: estado.interesPendiente > 0 ? '#b45309' : '#166534'
                        }}>
                          {moneda(Math.max(0, estado.interesPendiente))}
                        </td>
                        <td style={{ padding: '6px 0', textAlign: 'right', color: '#991b1b' }}>
                          {moneda(estado.capitalPendiente)}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280' }}>
                Se cubre primero el interés de todos: un préstamo con el interés sin
                cubrir sigue generando interés sobre el mismo capital, así que dejarlo
                sale más caro para los dos. Lo que sobre baja el capital{' '}
                <strong>empezando por los préstamos más pequeños</strong>, para cerrar
                los que se puedan cerrar del todo y no dejar {loans.length} cuentas a
                medias. La deuda total es la misma; lo que cambia es cuántas quedan
                abiertas.
              </div>
            </div>
          )}

          {cuentas && (
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '15px', marginBottom: '15px'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px', color: '#1f2937' }}>
                💵 Estado del préstamo
              </div>
              {/* El capital pendiente manda: de él sale el interés de cada
                  periodo, y es lo que el cliente lleva en su cuaderno. */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', fontSize: '14px' }}>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>Capital pendiente</div>
                  <strong style={{ color: cuentas.capitalPendiente > 0 ? '#ef4444' : '#10b981', fontSize: '16px' }}>
                    {moneda(cuentas.capitalPendiente)}
                  </strong>
                  {cuentas.abonadoACapital > 0 && (
                    <div style={{ color: '#9ca3af', fontSize: '11px' }}>
                      de {moneda(prestamo.amount)}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>
                    {cuentas.interesPendiente > 0 ? 'Interés que debe' : 'Interés por cobro'}
                  </div>
                  <strong style={{ color: cuentas.interesPendiente > 0 ? '#b45309' : '#1f2937' }}>
                    {moneda(cuentas.interesPendiente > 0 ? cuentas.interesPendiente : cuentas.interesPorCobro)}
                  </strong>
                  <div style={{ color: '#9ca3af', fontSize: '11px' }}>
                    {cuentas.interesPendiente > 0
                      ? `acumulado de ${cuentas.periodosGenerados} ${cuentas.periodosGenerados === 1 ? 'cobro' : 'cobros'}`
                      : `${prestamo.interest_rate}% del capital`}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#6b7280', fontSize: '12px' }}>Ya abonado</div>
                  <strong style={{ color: '#10b981' }}>
                    {moneda(cuentas.interesPagado + cuentas.abonadoACapital)}
                  </strong>
                  <div style={{ color: '#9ca3af', fontSize: '11px' }}>
                    {moneda(cuentas.interesPagado)} interés · {moneda(cuentas.abonadoACapital)} capital
                  </div>
                </div>
              </div>

              {cuentas.saldado && (
                <div style={{
                  marginTop: '10px', padding: '10px 12px', borderRadius: '6px',
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  color: '#166534', fontSize: '13px'
                }}>
                  ✅ El capital está saldado. Ya no genera intereses.
                </div>
              )}

              {!cuentas.saldado && (
                <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setTipoPago('interes');
                      setAmount(String(Math.round(Math.max(cuentas.interesPendiente, cuentas.interesPorCobro))));
                    }}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', border: '1px solid #c7d2fe',
                      background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontSize: '13px'
                    }}
                  >
                    Interés: {moneda(Math.max(cuentas.interesPendiente, cuentas.interesPorCobro))}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setTipoPago('capital'); setAmount(String(Math.round(cuentas.capitalPendiente))); }}
                    style={{
                      padding: '6px 12px', borderRadius: '6px', border: '1px solid #fecaca',
                      background: '#fef2f2', color: '#991b1b', cursor: 'pointer', fontSize: '13px'
                    }}
                  >
                    Saldar capital: {moneda(cuentas.capitalPendiente)}
                  </button>
                </div>
              )}

              {atraso && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  background: atraso.retrasado ? '#fef2f2' : '#f0fdf4',
                  color: atraso.retrasado ? '#991b1b' : '#166534',
                  border: `1px solid ${atraso.retrasado ? '#fecaca' : '#bbf7d0'}`
                }}>
                  {atraso.retrasado ? '🔴' : '🟢'}{' '}
                  {atraso.desdeUltimoPago
                    ? `Último pago hace ${atraso.dias} días`
                    : `Sin ningún pago desde que se otorgó, hace ${atraso.dias} días`}
                  {atraso.retrasado &&
                    ` · ${atraso.dias - atraso.periodo} días de atraso (se cobra cada ${atraso.periodo})`}
                </div>
              )}
            </div>
          )}

          {/* Qué se está abonando. Es la diferencia entre bajar la deuda o
              solo pagar el alquiler del dinero, así que va antes del monto y
              bien visible, no escondido en un desplegable. */}
          {prestamo && (
            <div className="form-group">
              <label>¿Qué está abonando? *</label>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {[
                  { valor: 'auto', titulo: '🔀 Repartir solo', pie: 'Cubre el interés, el resto al capital' },
                  { valor: 'interes', titulo: '💵 Interés', pie: 'No baja la deuda' },
                  { valor: 'capital', titulo: '📉 Capital', pie: 'Baja la deuda y el interés' }
                ].map(op => {
                  const activo = tipoPago === op.valor;
                  return (
                    <button
                      key={op.valor}
                      type="button"
                      onClick={() => elegirTipo(op.valor)}
                      style={{
                        flex: '1 1 150px',
                        padding: '12px',
                        borderRadius: '8px',
                        border: activo ? '2px solid #667eea' : '1px solid #e5e7eb',
                        background: activo ? '#eef2ff' : 'white',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <div style={{ fontWeight: 'bold', color: activo ? '#4338ca' : '#374151' }}>
                        {op.titulo}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        {op.pie}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>{tipoPago === 'auto' ? '¿Cuánto le entregó? ($) *' : 'Monto Pagado ($) *'}</label>
            <input
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ej: 100000"
              required
            />

            {/* Cuánto de lo entregado se cobra de interés.

                El prestamista manda: él pactó y él negocia por teléfono. A
                Javier Suárez le cobró $150.000 de interés de sus tres créditos
                cuando el calculado eran $91.000, porque dos se los prestó hace
                tres días. Sin este campo había que anotarlo a mano, préstamo
                por préstamo, y ahí es donde se pierde un peso.

                Vacío = el que calculó el programa, que es lo de siempre. */}
            {enTodos && (
              <div style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '13px', color: '#374151' }}>
                  ¿Cuánto de eso es interés?
                </label>
                {/* NUNCA proponer cobrar cero.

                    Decía «Déjelo vacío para cobrar $0» — presentando como la
                    opción por defecto la única que le hace perder plata al
                    prestamista. Cuando el programa todavía no ha generado
                    interés, lo que hay que ofrecer es el mes. */}
                <input
                  type="number"
                  step="0.01"
                  value={interesCobrado}
                  onChange={(e) => setInteresCobrado(e.target.value)}
                  placeholder={mesDeTodos > interesDeTodos
                    ? `${Math.round(mesDeTodos)}  ← el mes de los ${loans.length}`
                    : `Déjelo vacío para cobrar ${moneda(interesDeTodos)}`}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  El programa calculó <strong>{moneda(interesDeTodos)}</strong> de
                  interés entre sus {loans.length} préstamos.
                  {/* El mes completo, cuando no coincide con lo generado. Es la
                      cifra que él tiene en la cabeza, y tenerla aquí le ahorra
                      sacarla a mano préstamo por préstamo. */}
                  {mesDeTodos !== interesDeTodos && (
                    <> El mes completo de los {loans.length} sería{' '}
                      <strong>{moneda(mesDeTodos)}</strong>.</>
                  )}
                  {interesCobrado !== '' && parseFloat(interesCobrado) >= 0 &&
                   Math.round(parseFloat(interesCobrado)) !== Math.round(interesDeTodos) && (
                    <span style={{ color: '#b45309' }}>
                      {' '}Usted va a cobrar{' '}
                      <strong>
                        {moneda(Math.abs(parseFloat(interesCobrado) - interesDeTodos))}
                      </strong>{' '}
                      {parseFloat(interesCobrado) > interesDeTodos ? 'más' : 'menos'}.
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* El reparto, mientras se teclea. Quien cobra escribe un número y
                tiene que poder ver a dónde va antes de guardar: después son dos
                renglones en el historial y ya es tarde para discutirlo. */}
            {repartoTodos && (
              <div style={{
                marginTop: '10px', padding: '12px 14px', borderRadius: '8px',
                background: repartoTodos.sobrante > 0 ? '#fffbeb' : '#f0f9ff',
                border: `1px solid ${repartoTodos.sobrante > 0 ? '#fde68a' : '#bae6fd'}`,
                fontSize: '13px', color: '#0c4a6e'
              }}>
                <div style={{ marginBottom: '6px' }}>
                  <strong>Así quedarían repartidos {moneda(parseFloat(amount))}:</strong>
                </div>
                {repartoTodos.partes.map(p => {
                  // A QUÉ PORCENTAJE ESTÁ QUEDANDO ese interés.
                  //
                  // Poniendo $150.000 de interés a mano, al préstamo de $100.000 de
                  // hace tres días le tocaban $29.500 — el 29,5%, cuatro veces el mes
                  // pactado— y la pantalla solo enseñaba la cifra en pesos. Es la
                  // decisión del prestamista y él la negocia, pero tiene que poder
                  // verla: $29.500 no se lee como «cuatro meses de interés» y 29,5%
                  // sobre un 7% pactado, sí.
                  const base = Math.round(p.estado.capitalPendiente);
                  const pct = base > 0 && p.aInteres > 0 ? (p.aInteres / base) * 100 : null;
                  const tasa = Number(p.prestamo.interest_rate) || 0;
                  const porEncima = pct !== null && tasa > 0 && pct > tasa + 0.05;
                  return (
                  <div key={p.prestamo.id} style={{ padding: '3px 0' }}>
                    • {moneda(p.prestamo.amount)} del {formatearFecha(p.prestamo.start_date)}:{' '}
                    {p.aInteres > 0 && <><strong>{moneda(p.aInteres)}</strong> al interés</>}
                    {pct !== null && (
                      <span style={{ color: porEncima ? '#b45309' : '#6b7280',
                                     fontWeight: porEncima ? 700 : 400 }}>
                        {' '}({pct.toFixed(1).replace('.', ',')}%{porEncima ? ` sobre el ${tasa}% pactado` : ''})
                      </span>
                    )}
                    {p.aInteres > 0 && p.aCapital > 0 && ' · '}
                    {p.aCapital > 0 && <><strong>{moneda(p.aCapital)}</strong> al capital</>}
                    {/* «Se cierra», no «queda saldado». Saldado no dice si el
                        préstamo se cierra o se queda abierto en cero, y esa es
                        justo la diferencia que importa: el cierre no se
                        deshace. */}
                    {p.cierra && (
                      <span style={{ color: '#166534', fontWeight: 700 }}> ✅ SE CIERRA</span>
                    )}
                  </div>
                  );
                })}
                {/* En cuánto queda el cliente. Un reparto entre tres préstamos
                    no se revisa sumando de cabeza, y esta es la cifra que él da
                    por teléfono: «entonces quedo debiendo ochocientos». */}
                <div style={{ marginTop: '8px', paddingTop: '8px',
                              borderTop: '1px solid #bae6fd' }}>
                  Queda debiendo <strong>{moneda(repartoTodos.capitalDespues)}</strong> de capital
                  {repartoTodos.saldados > 0 && (
                    <span style={{ color: '#166534' }}>
                      {' '}· {repartoTodos.saldados === 1
                        ? 'un préstamo queda saldado'
                        : `${repartoTodos.saldados} préstamos quedan saldados`}
                    </span>
                  )}
                </div>

                {/* NO SE ESTÁ COBRANDO INTERÉS, y eso hay que gritarlo.
                    
                    A Rubén Sola le pasó: sus tres créditos cortaban más
                    adelante, así que el motor calculó $0, y sus $850.000 se
                    iban enteros al capital. El grande quedaba en $650.000 en
                    vez de $800.000. La cuenta no estaba mal —es que no se cobró
                    el mes— pero la pantalla lo dejaba pasar sin decir nada, y
                    un interés que no se cobra no se recupera después. */}
                {repartoTodos.interesTotal === 0 && repartoTodos.aCapital > 0 && (
                  <div style={{ marginTop: '8px', padding: '10px 12px',
                                background: '#fef2f2', border: '1px solid #fca5a5',
                                borderRadius: '8px', color: '#991b1b' }}>
                    ⚠️ <strong>No se está cobrando interés.</strong> A ninguno de sus{' '}
                    {loans.length} préstamos le ha cerrado el mes todavía, así que{' '}
                    {moneda(parseFloat(amount))} bajarían el capital enteros.
                    {mesDeTodos > 0 && (
                      <>
                        <div style={{ marginTop: '10px' }}>
                          {/* Un toque en vez de teclear.
                              
                              El aviso decia la cifra y aun asi se leia como un
                              error: el numero grande de «queda debiendo» manda
                              sobre cualquier renglon de texto. Con el boton, lo
                              correcto esta a un dedo y no hay que copiar nada
                              a mano — donde ademas se cuela un cero de mas. */}
                          <button
                            type="button"
                            onClick={() => setInteresCobrado(String(Math.round(mesDeTodos)))}
                            style={{ padding: '9px 16px', borderRadius: '8px',
                                     border: 'none', background: '#b91c1c', color: 'white',
                                     fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                            Cobrar el mes: {moneda(mesDeTodos)}
                          </button>
                        </div>
                        <div style={{ marginTop: '6px', fontSize: '12px' }}>
                          O escríbalo a mano arriba si va a cobrar otra cosa.
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* El interés de más solo se puede cobrar CERRANDO. En un
                    préstamo que sigue abierto manda el motor: cobra el mes
                    sobre el saldo al corte y reparte él lo que entró, mire lo
                    que mire la etiqueta del abono. */}
                {repartoTodos.extraSinDondeCobrar > 0 && (
                  <div style={{ marginTop: '6px', color: '#92400e' }}>
                    ⚠️ No se pueden cobrar {moneda(repartoTodos.extraSinDondeCobrar)} de interés
                    de más, porque con este pago no queda cerrado ningún préstamo. Se cobra{' '}
                    {moneda(repartoTodos.interesTotal)} —el del mes— y esa plata baja el capital.
                    Suba el monto hasta cerrar alguno si quiere cobrarlo.
                  </div>
                )}

                {/* Cobrar más interés del que entró dejaría al cliente debiendo
                    más POR HABER PAGADO. Se recorta y se dice. */}
                {repartoTodos.interesRecortado && (
                  <div style={{ marginTop: '6px', color: '#92400e' }}>
                    ⚠️ Pidió cobrar {moneda(parseFloat(interesCobrado))} de interés y solo
                    entraron {moneda(parseFloat(amount))}. No se puede cobrar más interés del
                    que entró: los pesos que faltan saldrían de la nada y el cliente quedaría
                    debiendo más por haber pagado.
                  </div>
                )}

                {repartoTodos.sobrante > 0 && (
                  <div style={{ marginTop: '6px', color: '#92400e' }}>
                    ⚠️ Sobran {moneda(repartoTodos.sobrante)}: es más de lo que debe en total.
                  </div>
                )}
              </div>
            )}

            {reparto && (
              <div style={{
                marginTop: '10px', padding: '12px 14px', borderRadius: '8px',
                background: reparto.sobrante > 0 ? '#fffbeb' : '#f0f9ff',
                border: `1px solid ${reparto.sobrante > 0 ? '#fde68a' : '#bae6fd'}`,
                fontSize: '13px', color: '#0c4a6e'
              }}>
                {reparto.alDiaEnInteres ? (
                  <>
                    <strong>El interés está al día.</strong> Los {moneda(parseFloat(amount))} bajan
                    el capital: quedaría en{' '}
                    <strong>{moneda(Math.max(0, cuentas.capitalPendiente - reparto.aCapital))}</strong>.
                  </>
                ) : (
                  <>
                    De {moneda(parseFloat(amount))}:{' '}
                    <strong>{moneda(reparto.aInteres)}</strong> cubren el interés
                    {reparto.aCapital > 0 ? (
                      <> y <strong>{moneda(reparto.aCapital)}</strong> bajan el capital.</>
                    ) : (
                      <>
                        {reparto.aInteres < cuentas.interesPendiente && (
                          <>, y quedan {moneda(cuentas.interesPendiente - reparto.aInteres)} de
                          interés sin cubrir</>
                        )}.
                      </>
                    )}
                  </>
                )}
                {reparto.sobrante > 0 && (
                  <div style={{ marginTop: '6px', color: '#92400e' }}>
                    ⚠️ Son {moneda(reparto.sobrante)} más de lo que debe.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>📅 Fecha del Pago *</label>
            <input
              type="date"
              value={fechaPago}
              onChange={(e) => setFechaPago(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="btn-primary" disabled={guardandoPago}
                  style={{ cursor: guardandoPago ? 'not-allowed' : 'pointer',
                           opacity: guardandoPago ? 0.7 : 1 }}>
            {guardandoPago ? 'Guardando…' : '💾 Registrar Pago'}
          </button>
        </form>
      </div>

      {/* Historial del cliente seleccionado */}
      {clienteElegido && (
        <div className="table-container">
          <h3>🧾 Pagos de {clienteElegido.name}</h3>
          {historialCliente.length === 0 ? (
            <p className="empty-state">Este cliente no ha hecho ningún pago todavía</p>
          ) : (
            <table className="payments-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Monto</th>
                  <th>Préstamo</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {historialCliente.map((p, idx) => (
                  <tr key={p.id}>
                    <td>{idx + 1}</td>
                    <td>{formatearFecha(p.payment_date)}</td>
                    <td>
                      <strong>{moneda(p.amount)}</strong>
                      <div style={{
                        fontSize: '11px', fontWeight: 'bold',
                        color: conceptoDelAbono(p.prestamo, p).color
                      }}>
                        {conceptoDelAbono(p.prestamo, p).texto}
                      </div>
                    </td>
                    <td style={{ color: '#6b7280', fontSize: '13px' }}>
                      {moneda(p.prestamo.amount)} · {p.prestamo.interest_rate}%
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {/* El papel que el cliente pide en la mano. Sale del
                          historial y no solo al registrar: el que hay que
                          reimprimir es siempre uno de hace rato. */}
                      <button
                        onClick={() => imprimirRecibo({
                          cliente: clienteElegido,
                          prestamo: p.prestamo,
                          pago: p,
                          pagos: pagosPorPrestamo[p.prestamo.id] || [],
                        })}
                        title="Imprimir el recibo de este abono"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '15px', padding: '4px'
                        }}
                      >
                        🧾
                      </button>
                      <button
                        onClick={() => abrirCorreccion(p)}
                        title="Corregir este pago"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '15px', padding: '4px'
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => borrarPago(p)}
                        title="Borrar este pago"
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: '15px', padding: '4px'
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan="2"><strong>Total pagado</strong></td>
                  <td colSpan="3">
                    <strong style={{ color: '#10b981' }}>
                      {moneda(historialCliente.reduce((s, p) => s + (p.amount || 0), 0))}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      )}

      {/* Historial general */}
      <div className="table-container">
        <h3>📜 Últimos Pagos Registrados</h3>
        {paymentHistory.length === 0 ? (
          <p className="empty-state">No hay pagos registrados aún</p>
        ) : (
          <table className="payments-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Cliente</th>
                <th>Monto</th>
                <th>Fecha Pago</th>
              </tr>
            </thead>
            <tbody>
              {paymentHistory.slice(0, 20).map((payment, idx) => (
                <tr key={payment.id}>
                  <td>{idx + 1}</td>
                  <td>{payment.client_name || 'N/A'}</td>
                  <td>{moneda(payment.amount)}</td>
                  <td>{formatearFecha(payment.payment_date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Ficha de corrección de un pago */}
      {pagoEditando && (
        <div
          onClick={() => !guardandoPago && setPagoEditando(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            padding: '40px 15px', zIndex: 100, overflowY: 'auto'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '12px', padding: '25px',
              width: '100%', maxWidth: '420px', boxShadow: '0 10px 40px rgba(0,0,0,.25)'
            }}
          >
            <h3 style={{ marginTop: 0 }}>✏️ Corregir pago</h3>
            <p style={{ color: '#6b7280', fontSize: '13px', marginTop: 0 }}>
              Registrado como {pagoEditando.tipo === 'capital' ? 'abono a capital' : 'interés'}
              {' '}por {moneda(pagoEditando.amount)} el{' '}
              {formatearFecha(pagoEditando.payment_date)}. Al guardar se recalcula
              el score del cliente y su estado de mora.
            </p>

            <form onSubmit={guardarCorreccion}>
              {/* Cada campo en su .form-group: sueltos, la hoja de estilos los
                  deja en línea y la etiqueta acaba pegada al recuadro. */}

              {/* Poder cambiar el concepto es lo que más falta hacía. Marcar un
                  abono como capital por error baja la deuda del cliente, y sin
                  esto había que borrarlo y volver a crearlo para arreglarlo. */}
              <div className="form-group">
                <label>¿Qué fue este abono?</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { valor: 'interes', texto: '💵 Interés' },
                    { valor: 'capital', texto: '📉 Capital' }
                  ].map(op => {
                    const activo = tipoEditado === op.valor;
                    return (
                      <button
                        key={op.valor}
                        type="button"
                        onClick={() => setTipoEditado(op.valor)}
                        style={{
                          flex: 1, padding: '10px', borderRadius: '7px',
                          border: activo ? '2px solid #667eea' : '1px solid #e5e7eb',
                          background: activo ? '#eef2ff' : 'white',
                          color: activo ? '#4338ca' : '#374151',
                          fontWeight: activo ? 'bold' : 'normal',
                          cursor: 'pointer', fontSize: '14px'
                        }}
                      >
                        {op.texto}
                      </button>
                    );
                  })}
                </div>
                {tipoEditado !== (pagoEditando.tipo === 'capital' ? 'capital' : 'interes') && (
                  <small style={{ color: '#b45309', display: 'block', marginTop: '6px' }}>
                    Al cambiarlo se recalcula la deuda: el capital la baja, el
                    interés no.
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Monto ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={montoEditado}
                  onChange={(e) => setMontoEditado(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label>Fecha del pago</label>
                <input
                  type="date"
                  value={fechaEditada}
                  onChange={(e) => setFechaEditada(e.target.value)}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '22px' }}>
                <button
                  type="button"
                  onClick={() => setPagoEditando(null)}
                  disabled={guardandoPago}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '6px', cursor: 'pointer',
                    border: '1px solid #e5e7eb', background: 'white', color: '#374151'
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={guardandoPago}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '6px', border: 'none',
                    background: '#667eea', color: 'white', fontWeight: 'bold',
                    cursor: guardandoPago ? 'not-allowed' : 'pointer',
                    opacity: guardandoPago ? 0.7 : 1
                  }}
                >
                  {guardandoPago ? 'Guardando...' : '💾 Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Payments;
